import { useQuery } from '@tanstack/react-query';
import { client } from '@xinfer/api-client';
import type { Account } from '@xinfer/api-client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  PageHeader,
  StatCard,
  StatGrid,
  usePageTitle,
} from '@xinfer/ui';
import { Activity, Database, Layers, Percent } from 'lucide-react';
import { TokenComposition } from '../components/TokenComposition';
import {
  cacheHitRate,
  explainKvTokenSeconds,
  formatInteger,
  formatPercent,
} from '../lib/format';

/**
 * What the user wants to know on landing: what have I spent, and how much room
 * do I have left.
 */
export function Overview({ account }: { account: Account }) {
  usePageTitle('Overview');

  const usage = useQuery({
    queryKey: ['usage', account.id],
    queryFn: () => client.usage(account.id),
  });

  const hitRate = usage.data
    ? cacheHitRate(usage.data.input_tokens, usage.data.cached_input_tokens)
    : null;

  return (
    <>
      <PageHeader
        title="Overview"
        description="Everything metered against this account in the current billing period."
      />

      {usage.isError && (
        <ErrorState
          title="Could not load usage"
          description="The figures below are read from the ledger, which is behind the api rather than on the serving path — inference is unaffected."
        />
      )}

      {!usage.isError && (
        <>
          <StatGrid>
            <StatCard
              label="Requests"
              value={usage.data ? formatInteger(usage.data.requests) : '—'}
              hint="completed inference calls"
              icon={<Activity />}
              loading={usage.isLoading}
            />
            <StatCard
              label="KV-token-seconds"
              value={usage.data ? formatInteger(usage.data.kv_token_seconds) : '—'}
              // The unit is right and unfamiliar; the portal is where that gets
              // explained, or every ticket will be about it.
              hint={usage.data ? explainKvTokenSeconds(usage.data.kv_token_seconds) : undefined}
              icon={<Database />}
              loading={usage.isLoading}
              explain="KV memory held, multiplied by how long it was held for. It is what the service actually charges on, because KV memory — not request count — is the scarce resource on this hardware."
            />
            <StatCard
              label="Peak KV tokens"
              value={usage.data ? formatInteger(usage.data.peak_kv_tokens) : '—'}
              hint="high-water mark, not a total"
              icon={<Layers />}
              loading={usage.isLoading}
              explain="The largest context held at one moment. Compare it against the plan's maximum context to see how close a workload runs to the cap."
            />
            <StatCard
              label="Cache hit rate"
              value={hitRate === null ? '—' : formatPercent(hitRate)}
              hint={
                usage.data
                  ? `${formatInteger(usage.data.cached_input_tokens)} of ${formatInteger(
                      usage.data.input_tokens,
                    )} prompt tokens`
                  : undefined
              }
              icon={<Percent />}
              loading={usage.isLoading}
              // Derived, not fetched, and the one figure on this page a user
              // can move: a cache hit skips prefill and is billed at its own
              // lower rate, so putting the stable part of a prompt first is
              // worth real money.
              explain="The share of prompt tokens served from the prefix cache. Cache hits skip prefill and are billed at a lower rate, so putting the unchanging part of a prompt first raises this."
            />
          </StatGrid>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Token composition</CardTitle>
                <CardDescription>
                  Prompt tokens split by cache outcome, against output. Cached prompt tokens are a
                  subset of the prompt total, never an addition to it.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {usage.data ? (
                  <TokenComposition usage={usage.data} />
                ) : (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Why KV-token-seconds?</CardTitle>
                <CardDescription>The unit the price list is built on.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
                <p>
                  The scarce resource on this service is KV memory, not requests. A session with a
                  512k-token context occupies roughly twenty times the hardware of an ordinary 25k
                  one, for as long as it is open — so that is what the meter reads.
                </p>
                <p>
                  Charging per output token would misprice a long-context session by about that
                  same factor, in both directions: short sessions would subsidise long ones, and a
                  workload that holds a large context while generating very little would pay
                  almost nothing for most of the hardware it occupies.
                </p>
              </CardContent>
            </Card>
          </div>

          <p className="mt-6 text-sm text-muted-foreground">
            Gaps between what the gate meters and what appears here are expected and bounded:
            usage ships on a cursor that only advances on acknowledgement, so figures lag rather
            than go missing.
          </p>
        </>
      )}
    </>
  );
}
